// src/ui/overlays/RoundResultCard.tsx — P7 Plan 09 (D-10).
//
// The deliberate round-end beat in the caught → spectate → result → next-round
// loop (NOT auto-restart, NOT back-to-title — D-10). Standalone + self-gating
// (TeamSwitchOverlay idiom): subscribes to `round-ended` and shows the outcome
// with a single Next round CTA; clears on `round-started`. App.tsx wiring (the
// CTA → round-restart path) is Plan 10.
//
// Outcome mapping (mirrors TeamSwitchOverlay pre-flip reasoning):
//   endReason === 'all-caught' → Hunters win
//   endReason === 'timer'      → Hiders win
//
// WARNING-15 tier-isolation: subscribes ONLY to the renderBus; the optional
// caption is a templated stat derived from the payload (caughtCount /
// totalHiders) — never an invented UI string.
//
// Source: 07-UI-SPEC §Surface 7; PATTERNS.md RoundResultCard analog.

import { useEffect, useRef, useState, type JSX } from 'react';

import { renderBus } from '../../render/bus';

import './round-result-card.css';

type WinSide = 'hunters' | 'hiders';

interface ResultState {
  outcome: string; // 'Hunters win' | 'Hiders win'
  side: WinSide;
  caughtCount: number;
  totalHiders: number;
}

export interface RoundResultCardProps {
  onNextRound: () => void;
}

export function RoundResultCard({ onNextRound }: RoundResultCardProps): JSX.Element | null {
  const [result, setResult] = useState<ResultState | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const offEnded = renderBus.on('round-ended', (e) => {
      const side: WinSide = e.endReason === 'all-caught' ? 'hunters' : 'hiders';
      setResult({
        outcome: side === 'hunters' ? 'Hunters win' : 'Hiders win',
        side,
        caughtCount: e.caughtCount,
        totalHiders: e.totalHiders,
      });
    });
    // Clear when the next round begins (the diegetic team-switch repositions).
    const offStarted = renderBus.on('round-started', () => setResult(null));
    return (): void => {
      offEnded();
      offStarted();
    };
  }, []);

  // Focus the Next round CTA when the beat appears (07-UI-SPEC §Surface 7 ARIA).
  useEffect(() => {
    if (result) btnRef.current?.focus();
  }, [result]);

  if (!result) return null;

  // Templated factual stat (07-UI-SPEC §Surface 7 — omit cleanly if no hiders).
  const caption =
    result.totalHiders > 0
      ? `${result.caughtCount} of ${result.totalHiders} hiders caught.`
      : null;

  return (
    <div className="round-result-backdrop" data-testid="round-result-backdrop">
      <div
        className="round-result-card"
        data-testid="round-result-card"
        data-side={result.side}
        role="dialog"
        aria-modal="true"
        aria-label={result.outcome}
      >
        <div className="round-result-card__accent" aria-hidden="true" />
        <h2 className="round-result-card__heading" data-testid="round-result-heading">
          {result.outcome}
        </h2>
        {caption !== null && (
          <p
            className="round-result-card__caption"
            data-testid="round-result-caption"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {caption}
          </p>
        )}
        <button
          ref={btnRef}
          type="button"
          className="round-result-card__cta"
          data-testid="round-result-next"
          onClick={onNextRound}
        >
          Next round
        </button>
      </div>
    </div>
  );
}
